package diana.soleil.design.iraniancanadienbazzar.core.common

/**
 * Forgiving text matching for the directory search.
 *
 * Exact substring matching fails the way people actually type: "Chateau Kebab" never finds
 * "Château Kabab" (QA 2026-09-12). [Formatters.foldForSearch] already levels accents, Persian/Arabic
 * letter variants and digits; this adds tolerance for a wrong or missing letter, word by word.
 *
 * Deliberately conservative: a token must still be close to a real word in the haystack. One edit is
 * allowed from four letters, two from eight — enough for Kabab/Kebab, Hosein/Hossein or a dropped
 * letter, not enough to turn a search into a guessing game. Short tokens (≤3) must match exactly,
 * since at that length everything is within one edit of everything.
 */
object SearchMatching {

    /** True when [foldedToken] appears in [foldedHaystack], allowing a near miss on a whole word. */
    fun matches(foldedHaystack: String, foldedToken: String): Boolean {
        if (foldedToken.isEmpty()) return true
        if (foldedHaystack.contains(foldedToken)) return true
        val tolerance = tolerance(foldedToken.length)
        if (tolerance == 0) return false
        return foldedHaystack.split(' ', '-', ',', '.', '/', '(', ')')
            .any { word -> word.isNotEmpty() && withinDistance(word, foldedToken, tolerance) }
    }

    /** How many single-character edits a token of this length may be off by. */
    fun tolerance(length: Int): Int = when {
        length <= 3 -> 0
        length <= 7 -> 1
        else -> 2
    }

    /**
     * Levenshtein distance, bounded: returns as soon as every cell in a row exceeds [max], so a long
     * haystack word costs little against a short token. Two rows of state, no matrix.
     */
    fun withinDistance(a: String, b: String, max: Int): Boolean {
        if (a == b) return true
        if (kotlin.math.abs(a.length - b.length) > max) return false
        var previous = IntArray(b.length + 1) { it }
        var current = IntArray(b.length + 1)
        for (i in 1..a.length) {
            current[0] = i
            var rowMin = current[0]
            for (j in 1..b.length) {
                val substitution = previous[j - 1] + if (a[i - 1] == b[j - 1]) 0 else 1
                current[j] = minOf(substitution, previous[j] + 1, current[j - 1] + 1)
                rowMin = minOf(rowMin, current[j])
            }
            if (rowMin > max) return false
            val swap = previous; previous = current; current = swap
        }
        return previous[b.length] <= max
    }
}
